// @ts-nocheck
import { useState, useCallback, useRef } from 'react';

// Feedback type mapping: internal key → API payload value
export const FEEDBACK_TYPE_MAP: Record<string, string> = {
  good: 'Good',
  partiallyCorrect: 'Partially Wrong',
  fullWrong: 'Fully Wrong',
  noFeedback: 'No Feedback',
  other: 'Other',
};

// ---- 1. Dashboard Stats ----
export const useFetchDashboardStats = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);

  const API_URL = process.env.REACT_APP_DASHBOARD_STATS_URL || '';

  const fetchDashboardStats = useCallback(async () => {
    if (!API_URL) return;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_URL, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`Failed to fetch dashboard stats: ${response.status}`);
      const result = await response.json();
      setData(result);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      console.error('Error fetching dashboard stats:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch dashboard stats');
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [API_URL]);

  return { data, loading, error, fetchDashboardStats };
};

// ---- 2. User Statistics ----
export const useFetchUserStatistics = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);

  const API_URL = process.env.REACT_APP_DASHBOARD_USER_STATISTICS_URL || '';

  const fetchUserStatistics = useCallback(
    async (username: string) => {
      if (!API_URL) return;
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      setData(null); // Clear stale data from previous user immediately
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username }),
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`Failed to fetch user statistics: ${response.status}`);
        const result = await response.json();
        setData(result);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        console.error('Error fetching user statistics:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch user statistics');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    },
    [API_URL]
  );

  return { data, loading, error, fetchUserStatistics };
};

// ---- 3. Feedback Conversations ----
export const useFetchFeedbackConversations = () => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);

  const API_URL = process.env.REACT_APP_DASHBOARD_FEEDBACK_CONVERSATIONS_URL || '';

  const fetchFeedbackConversations = useCallback(
    async (feedbackType: string, projectName = '', userName = '') => {
      if (!API_URL) return;
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ feedback_type: feedbackType, project_name: projectName, user_name: userName }),
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`Failed to fetch feedback conversations: ${response.status}`);
        const result = await response.json();
        setData(result);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        console.error('Error fetching feedback conversations:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch feedback conversations');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    },
    [API_URL]
  );

  return { data, loading, error, fetchFeedbackConversations };
};

// ---- 4. Project Stats ----
export const useFetchProjectStats = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);

  const API_URL = process.env.REACT_APP_DASHBOARD_PROJECTS_STATS_URL || '';

  const fetchProjectStats = useCallback(
    async (projectName: string) => {
      if (!API_URL) return;
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      setData(null); // Clear stale data from previous project immediately
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ projectName }),
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`Failed to fetch project stats: ${response.status}`);
        const result = await response.json();
        setData(result);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        console.error('Error fetching project stats:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch project stats');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    },
    [API_URL]
  );

  return { data, loading, error, fetchProjectStats };
};

// ---- 5. Queries Over Time ----
export const useFetchQueriesOverTime = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);

  const API_URL = process.env.REACT_APP_DASHBOARD_USAGE_TRENDS_URL || '';

  const fetchQueriesOverTime = useCallback(
    async (queriesStartDate: string, queriesEndDate: string) => {
      if (!API_URL) return;
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ queriesStartDate, queriesEndDate }),
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`Failed to fetch queries over time: ${response.status}`);
        const result = await response.json();
        setData(result);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        console.error('Error fetching queries over time:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch queries over time');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    },
    [API_URL]
  );

  return { data, loading, error, fetchQueriesOverTime };
};

// ---- 6. PII Conversations ----
export const useFetchPIIConversations = () => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);

  const API_URL = process.env.REACT_APP_DASHBOARD_PII_CONVERSATION_URL || '';

  const fetchPIIConversations = useCallback(
    async (projectName = '', userName = '') => {
      if (!API_URL) return;
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ project_name: projectName, user_name: userName }),
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`Failed to fetch PII conversations: ${response.status}`);
        const result = await response.json();
        setData(result?.data || result || []);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        console.error('Error fetching PII conversations:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch PII conversations');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    },
    [API_URL]
  );

  return { data, loading, error, fetchPIIConversations };
};

// ---- 7. Usage Trends ----
export const useFetchUsageTrends = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);

  const API_URL = process.env.REACT_APP_DASHBOARD_USAGE_TRENDS_URL || '';

  const fetchUsageTrends = useCallback(
    async (trendsStartDate: string, trendsEndDate: string) => {
      if (!API_URL) return;
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ trendsStartDate, trendsEndDate }),
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`Failed to fetch usage trends: ${response.status}`);
        const result = await response.json();
        setData(result);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        console.error('Error fetching usage trends:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch usage trends');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    },
    [API_URL]
  );

  return { data, loading, error, fetchUsageTrends };
};
